import dayjs from 'dayjs';
import TZ from 'dayjs/plugin/timezone';
import UTC from 'dayjs/plugin/utc';
import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { useState } from 'react';

import PlayCircleFilled from '@ant-design/icons/PlayCircleFilled';

import Layout from '~components/ui/Layout';
import Poster from '~components/ui/Poster';
import Select from '~components/ui/Select';
import { getFromAPI } from '~utils/api/common';
import normalizeNextReleases, {
  NormalizedNextReleases,
} from '~utils/normalizers/normalizeNextReleases';

import { Movie } from '~types';

import s from '../styles/pages/ComingSoon.module.css';

dayjs.extend(UTC);
dayjs.extend(TZ);
export const getStaticProps: GetStaticProps<{ movies: NormalizedNextReleases[] }> = async () => {
  const movies = await getFromAPI<Movie[]>('/movies/coming-soon?all');

  return {
    props: {
      movies: normalizeNextReleases(movies),
    },
    revalidate: 60 * 60 * 24, // 1 vez al dia
  };
};

type Props = InferGetStaticPropsType<typeof getStaticProps>;

const EmptyState = dynamic(() => import('~components/ui/EmptyState'), {
  ssr: false,
}) as typeof import('~components/ui/EmptyState').default;

const Movies: NextPage<Props> = ({ movies }) => {
  const movieFilter = (movie: NormalizedNextReleases) => {
    if (!genreFilter && !ratedFilter && !langFilter && !dateFilter) return movie;
    if (genreFilter !== '' && !movie.genre?.includes(genreFilter)) return;
    if (
      dateFilter !== '' &&
      dayjs(movie?.CinemaMovies?.[0].premiereAt).format('YYYY-MM') !== dateFilter
    ) {
      return;
    }
    if (ratedFilter !== '' && movie.rated !== ratedFilter) return;
    if (langFilter !== '' && movie.language !== langFilter) return;
    if (movie.genre === genreFilter || movie.genre?.includes(genreFilter)) return movie;
    if (dayjs(movie?.CinemaMovies?.[0].premiereAt).format('YYYY-MM') === dateFilter) return movie;
    if (movie.rated === ratedFilter) return movie;
    if (movie.language === langFilter) return movie;
  };
  const [genreFilter, setGenreFilter] = useState('');
  const [ratedFilter, setRatedFilter] = useState('');
  const [langFilter, setLangFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const groups: Record<string, NormalizedNextReleases[]> = {};
  const genres: string[] = [];
  const rates: string[] = [];
  const langs: string[] = [];
  const filteredMovies = movies.filter((movie) => {
    return movieFilter(movie);
  });
  // Generador de filtros
  movies.forEach((movie) => {
    const movieGenres = movie.genre?.split(',') || [];
    movieGenres.forEach((genre) => {
      if (genre && !genres.includes(genre.trim())) {
        genres.push(genre.trim());
      }
    });
    if (movie.rated && !rates.includes(movie.rated)) rates.push(movie.rated);
    if (movie.language && !langs.includes(movie.language)) langs.push(movie.language);
    const cinemaMovie = movie?.CinemaMovies?.sort((a, b) => {
      return dayjs(a.premiereAt).toDate().getTime() - dayjs(b.premiereAt).toDate().getTime();
    })[0];
    if (cinemaMovie) {
      const { premiereAt } = cinemaMovie;
      const date = dayjs(premiereAt).format('YYYY-MM');
      if (groups[date]) {
        groups[date].push(movie);
      } else {
        groups[date] = [movie];
      }
    }
  });

  return (
    <Layout meta={{ title: 'Próximos estrenos' }} headerType="translucent">
      {!movies?.length ? (
        <div className="container md:pb-4 md:pt-20 lg:pt-32">
          <EmptyState
            type="movie"
            title="No hay películas próximamente"
            description="Aquí encontrarás los próximos estrenos en el año."
          />
        </div>
      ) : (
        <>
          <div className="hero container md:pb-4 md:pt-20 lg:pt-24">
            <h1 className="h2 lg:big-title">Próximos estrenos</h1>
            <p>Descubre las películas que estarán próximamente en cartelera.</p>
          </div>
          <div className="container">
            <div className={s.FilterContainer}>
              <span className="basis-full text-lg lg:basis-auto">Filtros:</span>
              <Select
                placeholder="Género"
                className={s.FilterSelect}
                onChange={(e) => setGenreFilter(e.currentTarget.value)}
              >
                <option value="" hidden>
                  Género
                </option>
                <option value="">Todos los géneros</option>
                {genres.map((genre) => (
                  <option value={genre} key={genre}>
                    {genre}
                  </option>
                ))}
              </Select>
              <Select
                className={s.FilterSelect}
                onChange={(e) => setDateFilter(e.currentTarget.value)}
              >
                <option value="" hidden>
                  Mes
                </option>
                <option value="">Todos los meses</option>
                {Object.keys(groups).map((group) => {
                  const date = dayjs(group, 'YYYY-MM').format('MMMM YYYY');
                  return (
                    <option value={group} key={group}>
                      {`${date.charAt(0).toUpperCase()}${date.slice(1)}`}
                    </option>
                  );
                })}
              </Select>
              <Select
                className={s.FilterSelect}
                onChange={(e) => setRatedFilter(e.currentTarget.value)}
              >
                <option value="" hidden>
                  Clasificación
                </option>
                <option value="">Todas las clasificaciones</option>
                {rates.map((rate) => (
                  <option value={rate} key={rate}>
                    {rate}
                  </option>
                ))}
              </Select>
              {!!langs.length && (
                <Select
                  className={s.FilterSelect}
                  onChange={(e) => setLangFilter(e.currentTarget.value)}
                >
                  <option value="" hidden>
                    Idioma
                  </option>
                  <option value="">Todos los idiomas</option>
                  {langs.map((lang) => (
                    <option value={lang} key={lang}>
                      {lang}
                    </option>
                  ))}
                </Select>
              )}
            </div>
            {/* Regresa las películas filtradas, si no hay ningún filtro regresa todas y
            si ninguna cumple con los filtros regresa un EmptyState
        */}
            {!!filteredMovies.length ? (
              Object.keys(groups).map((date) => (
                <React.Fragment key={date}>
                  {!!filteredMovies.filter(
                    (movie) =>
                      dayjs(movie?.CinemaMovies?.[0].premiereAt).format('YYYY-MM') === date,
                  ).length && (
                    <>
                      <h2 className="h2 mb-6 text-center first-letter:uppercase md:text-left">
                        {dayjs(date, 'YYYY-MM').format('MMMM YYYY')}
                      </h2>
                      <div className={s.wrapper}>
                        {filteredMovies
                          .filter(
                            (movie) =>
                              dayjs(movie?.CinemaMovies?.[0].premiereAt).format('YYYY-MM') === date,
                          )
                          .map((movie) => {
                            const parsedSynopsis = movie?.synopsis;
                            const onPresale =
                              dayjs().isAfter(
                                dayjs(movie?.CinemaMovies?.[0]?.presaleStartAt).startOf('day'),
                              ) &&
                              dayjs().isBefore(
                                dayjs(movie?.CinemaMovies?.[0]?.presaleEndAt).endOf('day'),
                              );
                            return (
                              // COMING SOON CARD
                              // END COMING SOON CARD
                              <div key={movie.id}>
                                <div className={s.Card}>
                                  {movie?.poster && (
                                    <div>
                                      <Link href={`/pelicula/${movie.slug}`} className={s.Poster}>
                                        <Poster data={movie?.poster} version="url" />
                                        {onPresale && (
                                          <div className={s.presaleTagContainer}>
                                            <span className={s.presaleTag}>En Preventa</span>
                                          </div>
                                        )}
                                      </Link>
                                    </div>
                                  )}
                                  <div className="contents lg:block">
                                    <div className={s.DataContainer}>
                                      <h2 className="h5 line-clamp-2">{movie.title}</h2>
                                      <span className="text-sm">
                                        {movie?.originalTitle}{' '}
                                        {movie?.nationality && <span>({movie?.nationality})</span>}
                                      </span>
                                      <div>
                                        <div className="flex flex-wrap">
                                          {movie?.rated && (
                                            <span className={s.Tag}>{movie?.rated}</span>
                                          )}
                                          {movie?.genre && (
                                            <span className={s.Tag}>{movie?.genre}</span>
                                          )}
                                        </div>
                                      </div>
                                      <span className="text-sm text-primary dark:text-secondary">
                                        {movie?.CinemaMovies?.[0]?.previewAt
                                          ? `Preestreno: ${dayjs(
                                              movie?.CinemaMovies?.[0]?.previewAt,
                                            )
                                              .tz('America/Hermosillo')
                                              .format('DD [de] MMMM [del]' + ' YYYY')}`
                                          : `Estreno: ${dayjs(movie?.CinemaMovies?.[0]?.premiereAt)
                                              .tz('America/Hermosillo')
                                              .format('DD [de] MMMM [del]' + ' YYYY')}`}
                                      </span>
                                    </div>
                                    <div className={s.Wrapper}>
                                      <div
                                        className={s.Synopsis}
                                        dangerouslySetInnerHTML={{ __html: `${parsedSynopsis}` }}
                                      />
                                      <div className="grid grid-cols-2 space-x-2 lg:block">
                                        <Link
                                          href={`/pelicula/${movie?.slug}#trailer`}
                                          className="btn btn-primary   px-3"
                                        >
                                          <PlayCircleFilled />
                                          <span>Ver trailer</span>
                                        </Link>

                                        <Link
                                          href={`/pelicula/${movie.slug}`}
                                          className="btn dark:btn-dark"
                                        >
                                          VER DETALLE
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </>
                  )}
                </React.Fragment>
              ))
            ) : (
              <EmptyState
                type="movie"
                title="No hay películas próximamente"
                description="No hay péliculas que cumplen con tus filtros, intenta buscar con otros."
              />
            )}
          </div>
        </>
      )}
    </Layout>
  );
};

export default Movies;
